import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PaymentsIcon from '@mui/icons-material/Payments';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useNavigate } from 'react-router-dom';
import { fetchDashboardPendingTasks } from '../../utils/dashboardPendingTasks';
import DashboardWorkspaceCard from './DashboardWorkspaceCard';
import {
  getDashboardWorkspaceAvatarSx,
  getDashboardWorkspaceBadgeSx,
  getDashboardWorkspaceIconButtonSx,
  getDashboardWorkspaceItemMetaSx,
  getDashboardWorkspaceItemTitleSx,
  getDashboardWorkspaceRowSx,
  getDashboardWorkspaceScrollSx,
  getDashboardWorkspaceSectionHeaderSx,
  getDashboardWorkspaceSectionTitleSx,
  getDashboardWorkspaceSummaryChipSx,
  getDashboardWorkspaceTitleSx,
  getDashboardWorkspaceSubtitleSx,
} from '../../utils/dashboardWorkspaceLayout';
import { TASK_SECTION_META } from '../../utils/dashboardPendingTasksLayout';
import { canModifyBooking } from '../../utils/featurePermissions';

const SECTION_ICONS = {
  no_show_ota: ReportProblemIcon,
  web_checkin: HowToRegIcon,
  checkin: LoginIcon,
  payment: PaymentsIcon,
  checkout: LogoutIcon,
  housekeeping: CleaningServicesIcon,
};

const SECTION_ROUTES = {
  no_show_ota: '/booking/no-show/',
  web_checkin: '/booking/web-checkin/',
  checkin: '/booking/checkin/',
  payment: '/finance/payments/',
  checkout: '/booking/checkout/',
  housekeeping: '/housekeeping/',
};

function getInitials(name) {
  const parts = String(name || 'G').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'G';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function TaskRow({ task, themeMode, accentColor, onClick }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={getDashboardWorkspaceRowSx(themeMode, accentColor)}
    >
      <Avatar sx={getDashboardWorkspaceAvatarSx(accentColor)}>{getInitials(task.title)}</Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
          <Typography sx={getDashboardWorkspaceItemTitleSx(themeMode)} noWrap>
            {task.title}
          </Typography>
          {task.badgeLabel ? (
            <Chip size="small" label={task.badgeLabel} sx={getDashboardWorkspaceBadgeSx(task.badgeColor)} />
          ) : null}
        </Box>
        {task.subtitle ? (
          <Typography sx={getDashboardWorkspaceItemMetaSx(themeMode)} noWrap>
            {task.subtitle}
          </Typography>
        ) : null}
        {task.meta ? (
          <Typography sx={{ ...getDashboardWorkspaceItemMetaSx(themeMode), mt: 0.15 }} noWrap>
            {task.meta}
          </Typography>
        ) : null}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 16, color: 'rgba(148,163,184,0.7)', mt: 0.35 }} />
    </Box>
  );
}

function LoadingSkeleton({ themeMode }) {
  return (
    <Box sx={{ px: 1.25, py: 1 }}>
      {[1, 2, 3, 4].map((row) => (
        <Box key={row} sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
          <Skeleton
            variant="circular"
            width={30}
            height={30}
            animation="wave"
            sx={{ bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton
              variant="text"
              width="72%"
              height={16}
              animation="wave"
              sx={{ bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
            />
            <Skeleton
              variant="text"
              width="54%"
              height={14}
              animation="wave"
              sx={{ bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function DashboardPendingTasksPanel({
  themeMode = 'dark',
  refreshKey = 0,
  tasksData: controlledTasksData,
  tasksLoading: controlledTasksLoading,
  tasksRefreshing: controlledTasksRefreshing,
  onRefreshTasks,
  onCheckout,
  onCheckIn,
  onPayment,
  onHousekeeping,
  onOpenWebCheckInInvite,
  showCollapseControl = false,
  stackedLayout = false,
  onCollapse,
  onMetricsChange,
}) {
  const navigate = useNavigate();
  const isControlled = controlledTasksData !== undefined;
  const [loading, setLoading] = useState(!isControlled);
  const [refreshing, setRefreshing] = useState(false);
  const [initialized, setInitialized] = useState(isControlled);
  const [data, setData] = useState({ sections: [], totalCount: 0 });
  const initializedRef = useRef(isControlled);
  const prevRefreshKeyRef = useRef(refreshKey);

  const resolvedData = isControlled ? (controlledTasksData || { sections: [], totalCount: 0 }) : data;
  const resolvedLoading = isControlled ? Boolean(controlledTasksLoading) : loading;
  const resolvedRefreshing = isControlled ? Boolean(controlledTasksRefreshing) : refreshing;
  const resolvedInitialized = isControlled ? !controlledTasksLoading : initialized;

  const loadTasks = useCallback(async (isRefresh = false) => {
    if (isControlled) {
      onRefreshTasks?.();
      return;
    }

    if (isRefresh) {
      setRefreshing(true);
    } else if (!initializedRef.current) {
      setLoading(true);
    }

    try {
      const result = await fetchDashboardPendingTasks({ forceRefresh: isRefresh });
      setData(result);
    } catch {
      setData({ sections: [], totalCount: 0 });
    } finally {
      initializedRef.current = true;
      setInitialized(true);
      setLoading(false);
      setRefreshing(false);
    }
  }, [isControlled, onRefreshTasks]);

  useEffect(() => {
    if (isControlled) return undefined;
    const isRefresh = prevRefreshKeyRef.current !== refreshKey && initializedRef.current;
    prevRefreshKeyRef.current = refreshKey;
    loadTasks(isRefresh);
    return undefined;
  }, [isControlled, loadTasks, refreshKey]);

  useEffect(() => {
    onMetricsChange?.({ totalCount: resolvedData.totalCount });
  }, [resolvedData.totalCount, onMetricsChange]);

  const summaryChips = useMemo(
    () => resolvedData.sections.filter((section) => section.items.length > 0),
    [resolvedData.sections]
  );

  const handleTaskClick = (task) => {
    if (task.section === 'web_checkin' && (task.inviteId || task.invite?.id)) {
      if (onOpenWebCheckInInvite) {
        onOpenWebCheckInInvite(task.invite || { id: task.inviteId });
        return;
      }
      navigate('/booking/web-checkin/');
      return;
    }
    if (task.section === 'checkout' && task.bookingId && onCheckout) {
      onCheckout(task.bookingId);
      return;
    }
    if (task.section === 'checkin' && task.bookingId && onCheckIn) {
      onCheckIn(task.bookingId);
      return;
    }
    if (task.section === 'payment' && task.bookingId && onPayment) {
      onPayment(task.bookingId);
      return;
    }
    if (task.section === 'payment' && task.bookingId) {
      if (canModifyBooking()) {
        navigate(`/booking/edit/${task.bookingId}/`);
      }
      return;
    }
    if (task.section === 'housekeeping' && task.cleaningId && onHousekeeping) {
      onHousekeeping(task.cleaningId);
      return;
    }
    if (task.section === 'housekeeping' && task.maintenanceId) {
      navigate('/housekeeping/maintenance/');
      return;
    }
    navigate(SECTION_ROUTES[task.section] || '/dashboard/');
  };

  const handleRefresh = () => loadTasks(true);

  return (
    <DashboardWorkspaceCard
      themeMode={themeMode}
      title="Pending Tasks"
      subtitle="Today and overdue actions for your team"
      headerActions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {showCollapseControl && onCollapse ? (
            <Tooltip title="Hide pending tasks">
              <IconButton
                size="small"
                onClick={onCollapse}
                sx={{
                  ...getDashboardWorkspaceIconButtonSx(themeMode),
                  display: stackedLayout ? 'inline-flex' : { xs: 'none', lg: 'inline-flex' },
                }}
                aria-label="Hide pending tasks panel"
              >
                {stackedLayout ? (
                  <KeyboardDoubleArrowDownIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Refresh tasks">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={resolvedLoading || resolvedRefreshing}
                sx={getDashboardWorkspaceIconButtonSx(themeMode)}
              >
                {resolvedRefreshing ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      summaryChips={
        resolvedInitialized
          ? summaryChips.map((section) => {
              const meta = TASK_SECTION_META[section.key] || TASK_SECTION_META.checkout;
              return {
                key: section.key,
                label: `${section.items.length} ${section.label}`,
                color: meta.color,
              };
            })
          : []
      }
    >
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {resolvedRefreshing ? (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              height: 2,
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#4ce1b6',
                transition: 'transform 0.4s linear',
              },
            }}
          />
        ) : null}
        <Box
          sx={{
            ...getDashboardWorkspaceScrollSx(themeMode),
            opacity: resolvedRefreshing ? 0.72 : 1,
            transition: 'opacity 280ms ease',
            pointerEvents: resolvedRefreshing ? 'none' : 'auto',
          }}
        >
          {!resolvedInitialized && resolvedLoading ? (
            <Fade in timeout={280}>
              <Box>
                <LoadingSkeleton themeMode={themeMode} />
              </Box>
            </Fade>
          ) : resolvedData.totalCount === 0 ? (
            <Fade in timeout={320}>
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <TaskAltIcon sx={{ fontSize: 42, color: '#4ce1b6', mb: 1, opacity: 0.85 }} />
                <Typography sx={getDashboardWorkspaceTitleSx(themeMode)}>All caught up</Typography>
                <Typography sx={{ ...getDashboardWorkspaceSubtitleSx(themeMode), mt: 0.5 }}>
                  No pending check-outs, check-ins, payments, housekeeping cleanings, maintenance, or OTA no-shows right now.
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Fade in timeout={320}>
              <Box>
                {resolvedData.sections.map((section) => {
                  if (!section.items.length) return null;
                  const meta = TASK_SECTION_META[section.key] || TASK_SECTION_META.checkout;
                  const SectionIcon = SECTION_ICONS[section.key] || TaskAltIcon;

                  return (
                    <Box key={section.key}>
                      <Box sx={getDashboardWorkspaceSectionHeaderSx(themeMode, meta.color)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <SectionIcon sx={{ fontSize: 16, color: meta.color }} />
                          <Typography sx={getDashboardWorkspaceSectionTitleSx(themeMode)}>{section.label}</Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={section.items.length}
                          sx={getDashboardWorkspaceSummaryChipSx(themeMode, meta.color)}
                        />
                      </Box>

                      {section.items.map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          themeMode={themeMode}
                          accentColor={meta.color}
                          onClick={() => handleTaskClick(task)}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </DashboardWorkspaceCard>
  );
}
